import { useState, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RedirectLink } from '../shared/RedirectLink';
import { Button, ConfirmModal, Badge, showToast } from '../shared';
import { PROJECT_GRADIENTS } from '../../constants/gradients';
import { useProfileDetails } from '../../lib/react-query/profile';
import {
  useAcceptDashboardInvite,
  useCancelDashboardInvite,
  useRejectDashboardInvite,
} from '../../lib/react-query/projects';
import { extractBackendErrorMessage, toUsDate } from '../../utils/utils';
import OwnerAttribution from './OwnerAttribution';
import { cn } from '../../lib/cn';

// Deterministic per-project pattern so each card gets a stable, distinct motif.
const PATTERNS = ['dots', 'diagonals', 'rings', 'waves', 'grid'];

const pickPattern = (id) => PATTERNS[Math.abs(Number(id || 0)) % PATTERNS.length];

const Motif = ({ id }) => {
  const pattern = pickPattern(id);
  const cls = 'absolute inset-0 text-white/10 [mask-image:linear-gradient(to_bottom_right,black,transparent)]';

  if (pattern === 'dots') {
    return (
      <svg className={cls} aria-hidden>
        <defs>
          <pattern id={`p-dots-${id}`} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.5" fill="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#p-dots-${id})`} />
      </svg>
    );
  }
  if (pattern === 'diagonals') {
    return (
      <svg className={cls} aria-hidden>
        <defs>
          <pattern id={`p-dia-${id}`} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="12" stroke="currentColor" strokeWidth="1.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#p-dia-${id})`} />
      </svg>
    );
  }
  if (pattern === 'rings') {
    return (
      <svg className={cls} aria-hidden>
        <defs>
          <pattern id={`p-rings-${id}`} width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="14" cy="14" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="14" cy="14" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#p-rings-${id})`} />
      </svg>
    );
  }
  if (pattern === 'waves') {
    return (
      <svg className={cls} aria-hidden preserveAspectRatio="none">
        <defs>
          <pattern id={`p-waves-${id}`} width="40" height="16" patternUnits="userSpaceOnUse">
            <path d="M0 8 Q 10 0 20 8 T 40 8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#p-waves-${id})`} />
      </svg>
    );
  }
  return (
    <svg className={cls} aria-hidden>
      <defs>
        <pattern id={`p-grid-${id}`} width="18" height="18" patternUnits="userSpaceOnUse">
          <path d="M18 0 L0 0 L0 18" fill="none" stroke="currentColor" strokeWidth="1.25" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#p-grid-${id})`} />
    </svg>
  );
};
const ProjectCard = memo(function ProjectCard({ project, variants }) {
  const navigate = useNavigate();
  const { data: currentUser } = useProfileDetails(true);
  const { mutateAsync: acceptInvite, isPending: isAccepting } = useAcceptDashboardInvite();
  const { mutateAsync: rejectInvite, isPending: isRejecting } = useRejectDashboardInvite();
  const { mutateAsync: cancelInvite, isPending: isCancelling } = useCancelDashboardInvite();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const progress = project?.completion_percentage > 0 ? project.completion_percentage : 8;

  const gradientClass = PROJECT_GRADIENTS[project?.header_gradient] || "from-secondary to-primary";
  const isPendingInvitation = project?.user_membership_status === 'PENDING';
  const isExpired = isPendingInvitation && project?.invitation_expires_at && new Date(project.invitation_expires_at) <= new Date();
  const isActive = project?.status === 'ACTIVE';

  const formatDate = (value) => {
    return toUsDate(value) || "TBD";
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!project?.member_id) {
      showToast('Invalid invitation', 'error', { position: 'top-center' });
      return;
    }

    try {
      const resp = await acceptInvite({ memberId: project.member_id });
      const message = resp?.message || 'Invitation accepted successfully';
      showToast(message, 'success', { position: 'top-center' });
    } catch (err) {
      const message = extractBackendErrorMessage(err, 'Failed to accept invitation');
      showToast(message, 'error', { position: 'top-center' });
    }
  };

  const openRejectModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowRejectModal(true);
  };

  const handleRejectInvitation = async () => {
    if (!project?.member_id) {
      showToast('Invalid invitation', 'error', { position: 'top-center' });
      return;
    }

    try {
      const resp = await rejectInvite({ memberId: project.member_id });
      const message = resp?.message || 'Invitation rejected successfully';
      showToast(message, 'success', { position: 'top-center' });
      setShowRejectModal(false);
    } catch (err) {
      const message = extractBackendErrorMessage(err, 'Failed to reject invitation');
      showToast(message, 'error', { position: 'top-center' });
    }
  };

  const openCancelModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCancelModal(true);
  };

  const handleCancelInvitation = async () => {
    if (!project?.member_id) {
      showToast('Invalid invitation', 'error', { position: 'top-center' });
      return;
    }

    try {
      const resp = await cancelInvite({ memberId: project.member_id, projectId: project.id });
      const message = resp?.message || 'Expired invitation cancelled successfully.';
      showToast(message, 'success', { position: 'top-center' });
      setShowCancelModal(false);
    } catch (err) {
      const message = extractBackendErrorMessage(err, 'Failed to cancel invitation');
      showToast(message, 'error', { position: 'top-center' });
    }
  };

  if (isPendingInvitation) {
    return (
      <>
        <ConfirmModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          onConfirm={handleRejectInvitation}
          title="Reject Invitation"
          message={`Are you sure you want to reject the invitation to join "${project?.title}"?`}
          confirmText="Reject"
          isLoading={isRejecting}
        />
        <ConfirmModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelInvitation}
          title="Cancel expired invitation"
          message={`Cancel the expired invitation to join "${project?.title}"?`}
          confirmText="Cancel invitation"
          confirmColor="danger"
          isLoading={isCancelling}
        />
        <motion.div
          variants={variants}
          className="relative border-2 border-dashed border-surface-border rounded-2xl bg-surface/50 overflow-hidden hover:border-mediumgrey transition-colors h-full flex flex-col"
        >
          <div className={cn("bg-gradient-to-r opacity-70 grayscale", gradientClass)}>
            <Motif id={project.id} />
          <h3 className="relative z-10 px-4 pb-8 pt-5 font-sans text-lg font-semibold text-white capitalize text-balance">{project.title}</h3>
            <div className="px-2 pb-4">
              <OwnerAttribution project={project} currentUser={currentUser} />
            </div>
          </div>

          <div className="w-full h-1 bg-surface-active">
            <div className="h-full bg-mediumgrey" style={{ width: `${progress}%` }} />
          </div>

          <div className="p-4 pt-8 border-t border-surface-border flex-1 flex flex-col justify-end">
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              {isExpired ? (
                <>
                  <p className="text-sm font-medium text-danger">Invitation Expired</p>
                  <p className="text-xs text-mediumgrey">This invitation is no longer valid</p>
                  <Button
                    variant="outlined"
                    color="danger"
                    size="sm"
                    onClick={openCancelModal}
                    disabled={isCancelling}
                    loading={isCancelling}
                    className="w-full"
                  >
                    Cancel invitation
                  </Button>
                </>
              ) : (
                <>
                  <div className="bg-white px-3 py-1 rounded-full border border-surface-border shadow-sm mb-2">
                    <p className="text-xs font-semibold text-darkgrey">Pending Invitation</p>
                  </div>
                  <div className="flex gap-2 w-full">
                    <Button
                      variant="outlined"
                      color="danger"
                      size="sm"
                      onClick={openRejectModal}
                      disabled={isAccepting || isRejecting}
                      className="flex-1"
                    >
                      Reject
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleAcceptInvitation}
                      loading={isAccepting}
                      disabled={isAccepting || isRejecting}
                      className="flex-1"
                    >
                      Accept
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <motion.div
      variants={variants}
      whileHover={{ y: -5, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className="h-full"
    >
      <Link
        to={`/projects/${project.id}/tasks`}
        aria-label={`View ${project?.title || 'project'} details`}
        className="group block h-full border border-surface-border/60 rounded-2xl bg-white shadow-sm hover:shadow-xl hover:shadow-surface-active/50 transition-all duration-300 select-none overflow-hidden ring-1 ring-primary/5 flex flex-col relative"
      >
        <div className={cn("relative bg-gradient-to-br overflow-hidden", gradientClass)}>
          {/* Glass effect overlay */}
          <div className="absolute inset-0 bg-white/10 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Decorative shapes */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/5 rounded-full blur-2xl" />

          <Motif id={project.id} />
          <div className="relative z-10 px-5 pb-6 pt-6 flex items-start justify-between gap-3">
            <h3 className="font-sans text-lg font-bold text-white capitalize tracking-wide text-balance shadow-black/5 drop-shadow-md leading-tight">
              {project.title}
            </h3>
            {project.team_name && (
              <span
                title={project.team_name}
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white border border-white/25 backdrop-blur-sm"
              >
                {project.team_is_personal ? 'Personal' : project.team_name}
              </span>
            )}
          </div>
          <div className="relative z-10 px-4 pb-4">
            <OwnerAttribution project={project} currentUser={currentUser} />
          </div>
        </div>

        <div className="relative w-full h-1 bg-surface-hover overflow-hidden">
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: progress / 100 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            style={{ transformOrigin: "left" }}
            className={cn("absolute left-0 top-0 h-full w-full bg-gradient-to-r", gradientClass)}
          />
        </div>

        <div className="p-4 border-t border-surface-border flex-1 flex flex-col justify-end gap-3">
          {isActive ? (
            <div className="flex items-center justify-between text-xs font-medium text-mediumgrey">
              <Badge color="success" size="sm" dot={true} className="uppercase tracking-wider font-bold">
                Active
              </Badge>
              <span className="font-medium tracking-tight text-mediumgrey text-[10px]">
                {formatDate(project?.start_date)} - {formatDate(project?.end_date)}
              </span>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <span className="text-xs text-mediumgrey">
                {project?.member_count ?? project?.members?.length ?? 0} members
              </span>
            </div>
            <div className="transform group-hover:translate-x-1 transition-transform duration-300">
              <RedirectLink
                text="View"
                sx="text-darkgrey group-hover:text-primary font-medium"
                iconClassName="!fill-grey group-hover:!fill-primary transition-colors"
                onClick={() => navigate(`/projects/${project.id}/tasks`)}
              />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
});

export { ProjectCard };
export default ProjectCard;
